import React, { useRef, useState } from 'react';
import { Avatar, Box, CircularProgress, IconButton, Tooltip } from '@mui/material';
import PersonIcon from '@mui/icons-material/Person';
import PhotoCameraOutlined from '@mui/icons-material/PhotoCameraOutlined';
import HighlightOffOutlined from '@mui/icons-material/HighlightOffOutlined';
import { useTranslation } from 'react-i18next';
import { uploadAssetFile } from '../utils/uploadAssetFile';

const AVATAR_PX = 152;
const ACTION_BTN_PX = 44;

export interface ProfilePhotoPickerProps {
  value: string | undefined;
  /** New image URL after upload, or `null` when the user removes the photo. */
  onUrlChange: (url: string | null) => void;
  onError?: (message: string) => void;
  disabled?: boolean;
  /** `start` aligns left for side-by-side profile layouts; default centers the picker. */
  layout?: 'centered' | 'start';
}

export const ProfilePhotoPicker: React.FC<ProfilePhotoPickerProps> = ({
  value,
  onUrlChange,
  onError,
  disabled = false,
  layout = 'centered',
}) => {
  const { t } = useTranslation();
  const inputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);

  const hasPhoto = Boolean(value?.trim());

  const handlePick = () => inputRef.current?.click();

  const handleRemove = (e: React.MouseEvent) => {
    e.stopPropagation();
    onUrlChange(null);
  };

  const handleFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      onError?.(t('profile.photoInvalidType', 'Please choose an image file.'));
      return;
    }
    setUploading(true);
    try {
      const url = await uploadAssetFile(file);
      onUrlChange(url);
    } catch {
      onError?.(t('profile.photoUploadError', 'Could not upload photo. Try again.'));
    } finally {
      setUploading(false);
    }
  };

  const changeLabel = t('profile.changeProfilePhoto', 'Change photo');
  const removeLabel = t('profile.removeProfilePhoto', 'Remove photo');

  const isStartLayout = layout === 'start';

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: isStartLayout ? { xs: 'center', md: 'flex-start' } : 'center',
        width: isStartLayout ? 'auto' : '100%',
        mb: isStartLayout ? 0 : { xs: 3, md: 4 },
      }}
    >
      <Box
        sx={{
          position: 'relative',
          width: AVATAR_PX,
          height: AVATAR_PX,
          flexShrink: 0,
        }}
      >
        <input
          ref={inputRef}
          type="file"
          accept="image/*"
          hidden
          onChange={handleFile}
          aria-hidden
        />
        <Avatar
          src={value || undefined}
          alt=""
          sx={{
            width: AVATAR_PX,
            height: AVATAR_PX,
            margin: '0 auto',
            boxShadow: (theme) =>
              theme.palette.mode === 'dark'
                ? `0 0 0 1px ${theme.palette.divider}, 0 4px 20px rgba(0,0,0,0.35)`
                : `0 0 0 1px ${theme.palette.divider}, 0 4px 16px rgba(0,0,0,0.08)`,
          }}
        >
          {!hasPhoto ? <PersonIcon sx={{ fontSize: AVATAR_PX * 0.45 }} aria-hidden /> : null}
        </Avatar>
        {uploading ? (
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: (theme) => theme.palette.action.hover,
              backdropFilter: 'blur(2px)',
            }}
            aria-live="polite"
            aria-busy="true"
          >
            <CircularProgress size={40} thickness={4} aria-label={t('profile.uploadingPhoto', 'Uploading…')} />
          </Box>
        ) : null}
        {hasPhoto && !uploading ? (
          <Tooltip title={removeLabel} placement="top" arrow>
            <span>
              <IconButton
                type="button"
                onClick={handleRemove}
                disabled={disabled}
                aria-label={removeLabel}
                sx={{
                  position: 'absolute',
                  left: 0,
                  top: 0,
                  width: ACTION_BTN_PX,
                  height: ACTION_BTN_PX,
                  bgcolor: (theme) => theme.palette.error.main,
                  color: (theme) => theme.palette.error.contrastText,
                  boxShadow: 3,
                  border: (theme) => `2px solid ${theme.palette.background.paper}`,
                  '&:hover': {
                    bgcolor: (theme) => theme.palette.error.dark,
                  },
                  '&.Mui-disabled': {
                    bgcolor: 'action.disabledBackground',
                    color: 'action.disabled',
                  },
                }}
              >
                <HighlightOffOutlined sx={{ fontSize: 22 }} />
              </IconButton>
            </span>
          </Tooltip>
        ) : null}
        <Tooltip title={changeLabel} placement="top" arrow>
          <span>
            <IconButton
              type="button"
              onClick={handlePick}
              disabled={disabled || uploading}
              color="primary"
              aria-label={changeLabel}
              sx={{
                position: 'absolute',
                right: 0,
                bottom: 0,
                width: ACTION_BTN_PX,
                height: ACTION_BTN_PX,
                bgcolor: 'primary.main',
                color: 'primary.contrastText',
                boxShadow: 3,
                border: (theme) => `2px solid ${theme.palette.background.paper}`,
                '&:hover': {
                  bgcolor: 'primary.dark',
                },
                '&.Mui-disabled': {
                  bgcolor: 'action.disabledBackground',
                  color: 'action.disabled',
                },
              }}
            >
              <PhotoCameraOutlined sx={{ fontSize: 22 }} />
            </IconButton>
          </span>
        </Tooltip>
      </Box>
    </Box>
  );
};
